import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { toast } from "sonner";
import type { StockMovement, Ingredient } from "./useIngredients";

interface StockMovementWithDetails extends StockMovement {
  ingredients: Ingredient | null;
  products: {
    id: string;
    name: string;
  } | null;
  expenses?: {
    id: string;
    title: string;
    status: "open" | "paid" | "cancelled";
  } | null;
}

interface UseStockMovementsParams {
  ingredientId?: string;
  productId?: string;
  page?: number;
  pageSize?: number;
  startDate?: Date;
  endDate?: Date;
  movementType?: "entry" | "exit" | "sale" | "adjustment" | "all";
  itemCategory?: "ingredient" | "product" | "all";
}

export function useStockMovements(params: UseStockMovementsParams = {}) {
  const { activeRestaurant } = useRestaurantContext();
  const [movements, setMovements] = useState<StockMovementWithDetails[]>([]);
  const [totalMovements, setTotalMovements] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const { 
    ingredientId, 
    productId, 
    page = 1, 
    pageSize = 20,
    startDate,
    endDate,
    movementType = "all",
    itemCategory = "all",
  } = params;

  const fetchMovements = useCallback(async () => {
    if (!activeRestaurant) {
      setMovements([]);
      setTotalMovements(0);
      return;
    }

    setIsLoading(true);
    try {
      let query = supabase
        .from("stock_movements" as any)
        .select("*, ingredients(*), products(id, name), expenses(id, title, status)", { count: "exact" })
        .eq("restaurant_id", activeRestaurant.id)
        .order("created_at", { ascending: false });

      if (ingredientId) {
        query = query.eq("ingredient_id", ingredientId);
      }
      
      if (productId) {
        query = query.eq("product_id", productId);
      }

      // Filter by date range
      if (startDate) {
        query = query.gte("created_at", startDate.toISOString());
      }
      if (endDate) {
        // Set to end of day
        const endOfDay = new Date(endDate);
        endOfDay.setHours(23, 59, 59, 999);
        query = query.lte("created_at", endOfDay.toISOString());
      }

      // Filter by movement type
      if (movementType !== "all") {
        query = query.eq("type", movementType);
      }

      // Filter by item category
      if (itemCategory === "ingredient") {
        query = query.not("ingredient_id", "is", null);
      } else if (itemCategory === "product") {
        query = query.not("product_id", "is", null);
      }

      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      query = query.range(from, to);

      const { data, error, count } = await query as any;

      if (error) throw error;

      setMovements(data as StockMovementWithDetails[]);
      setTotalMovements(count || 0);
    } catch (error) {
      console.error("Error fetching stock movements:", error);
      toast.error("Erro ao carregar movimentações");
    } finally {
      setIsLoading(false);
    }
  }, [activeRestaurant, ingredientId, productId, page, pageSize, startDate, endDate, movementType, itemCategory]);

  const getMovementTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      entry: "Entrada",
      exit: "Saída",
      adjustment: "Ajuste",
      sale: "Venda",
    };
    return labels[type] || type;
  };

  return {
    movements,
    totalMovements,
    totalPages: Math.ceil(totalMovements / pageSize),
    isLoading,
    fetchMovements,
    getMovementTypeLabel,
  };
}
